import { useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { ErrorCode } from "@trulyinbox/errors";
import { ApiError } from "@/lib/api";
import { useAuthStore } from "@/lib/stores/auth";
import { useFirebaseData } from "@/lib/firebase/use-firebase-data";
import {
  FIREBASE_BULK_JOBS_COLLECTION,
  FIREBASE_BULK_IMPORTS_COLLECTION,
} from "@/lib/firebase/firebase-constants";
import { env } from "@/lib/env";
import {
  checkDns,
  connectSmtp,
  deleteEmailAccount,
  disconnectAccount,
  enableWarmup,
  pauseWarmup,
  reconnectAccount,
  resumeWarmup,
  listAccounts,
  listEmailAccountDomains,
  lookupEmailAccounts,
  testSmtpConnection,
  getActivityCycles,
  getActivityCyclesBulk,
  getActivityDaySummary,
  getEmailAccount,
  getEmailAccountOverview,
  getEmailAccountScores,
  getMailboxHistory,
  getProviderPresets,
  getSmtpConfig,
  isStoredPasswordUnreadable,
  getWarmupSettings,
  updateSmtp,
  updateWarmupSettings,
  uploadBulkCsv,
  getBulkImportStatus,
  startBulkOperation,
  getBulkJobStatus,
  listTags,
  setAccountTags,
  MAILBOX_HISTORY_LIMIT,
  ACTIVITY_CYCLES_INITIAL_LIMIT,
  ACTIVITY_CYCLES_LIMIT,
  type ActivityCyclesBulkResponse,
  type ActivityCyclesResponse,
  type ActivityDaySummaryResponse,
  type BulkConnectResult,
  type BulkImportStatus,
  type BulkJobResponse,
  type BulkJobStatus,
  type ConnectSmtpPayload,
  type EmailAccount,
  type DomainSecurity,
  type EmailAccountDomain,
  type EmailAccountOverview,
  type ListEmailAccountsParams,
  type MailboxHistoryResponse,
  type PaginatedEmailAccounts,
  type ProviderPreset,
  type ReconnectSmtpPayload,
  type SmtpConfig,
  type SmtpTestResult,
  type BulkSelection,
  type StartBulkOperationBody,
  type TestSmtpPayload,
  type UpdateSmtpPayload,
  type WarmupSettings,
  type WorkspaceTag,
  type UpdateWarmupSettingsPayload,
} from "@/lib/email-accounts";
import {
  getColumnPreference,
  updateColumnPreference,
  type ColumnPreference,
  type ColumnTableKey,
} from "@/lib/preferences";
import { postAllAccountsWarmupExport, type EspValue, type WarmupExportDays } from "@/lib/reports";
import { onboardingKeys } from "@/features/onboarding/queries";
import {
  patchDwdConnectedEmailSenderName,
  patchDwdConnectedEmailWarmup,
} from "@/features/dwd-workspace/dwd-cache";
import { orgKeys } from "@/features/settings/queries";
import { billingKeys } from "@/features/billing/queries";
import {
  getSaleshandyLinkStatus,
  linkSaleshandyAccount,
  type SaleshandyLinkStatus,
  type SaleshandyRetroactiveLinkResult,
} from "@/lib/saleshandy";
import { notifyApiError, notifySuccess, notifyWarning } from "@/lib/toast";
import { accountStatusBucket, isSuspendedBucket } from "./account-status";
import { patchAccountInLists } from "./list-cache-patch";
import { useIsBulkDeletePolling } from "./bulk-job-store";

/** The HighAdvocacy review-rewards prompt on a mailbox's Overview (`detail/use-review-reward-prompt.ts`). */
export const reviewRewardKeys = {
  eligibility: (accountId: number) => ["review-rewards", "eligibility", accountId] as const,
};

/**
 * Hierarchical query-key factory for the email-accounts domain.
 *
 * Why hierarchical: invalidating `emailAccountKeys.all` wipes lists *and*
 * details in one call after a destructive mutation, without us having to
 * remember every list/filter variant we created.
 *
 * Conventions:
 *  - `all` is the bare scope tuple — used as the root for `invalidateQueries`.
 *  - `lists` / `details` / `overviews` are intermediate scopes (kind).
 *  - The leaf functions (`list(filters)`, `detail(id)`, `overview(id)`)
 *    accept the variant keys and produce the full tuple used by `useQuery`.
 */
export const emailAccountKeys = {
  all: ["email-accounts"] as const,
  lists: () => [...emailAccountKeys.all, "list"] as const,
  list: (filters: Record<string, unknown> = {}) =>
    [...emailAccountKeys.lists(), filters] as const,
  details: () => [...emailAccountKeys.all, "detail"] as const,
  detail: (id: number) => [...emailAccountKeys.details(), id] as const,
  overview: (id: number) => [...emailAccountKeys.all, "overview", id] as const,
  scores: (id: number, days: number) =>
    [...emailAccountKeys.all, "scores", id, days] as const,
  smtpConfig: (id: number) =>
    [...emailAccountKeys.all, "smtp-config", id] as const,
  warmupSettings: (id: number) =>
    [...emailAccountKeys.all, "warmup-settings", id] as const,
  providerPresets: () => [...emailAccountKeys.all, "provider-presets"] as const,
  /** Mailbox-switcher list, keyed by search term (TRU-1250). Under `all` so a
   * destructive mutation's `invalidateQueries(all)` clears it — a deleted
   * mailbox must not linger in the switcher. */
  lookup: (q: string) => [...emailAccountKeys.all, "lookup", q] as const,
  /** Distinct workspace domains + per-domain account counts — backs the
   * filter drawer's Domain dropdown. Flat, filter-less key (workspace-wide,
   * not scoped to the current draft — see `useEmailAccountDomainsQuery`). */
  domains: () => [...emailAccountKeys.all, "domains"] as const,
  /** The Action items page (TRU-1257). Under `all` so a destructive mutation's
   * `invalidateQueries(all)` clears it; the warmup-state and DNS-check paths
   * invalidate it explicitly (see `propagateAccountUpdate`). Workspace-agnostic
   * like every key here — the tenant-switch invalidate refetches it. */
  actionItems: (filters: Record<string, unknown> = {}) =>
    [...emailAccountKeys.all, "action-items", filters] as const,
  bulkImport: (importId: string) =>
    [...emailAccountKeys.all, "bulk-import", importId] as const,
  /** Active async bulk-job status (one-shot seed fetch, then Firestore-live). */
  bulkJob: (jobId: string) =>
    [...emailAccountKeys.all, "bulk-job", jobId] as const,
  /** Workspace tag catalog (pickers + the tags filter). */
  tags: () => [...emailAccountKeys.all, "tags"] as const,
  /** 5-day activity day-summary window (counts only). No pagination
   * dimension — a day's own cycles live under a separate key
   * (`activityCycles`), fetched lazily per day. `engagedOnly` is in the key
   * because it changes WHICH days are listed. */
  activityDaySummary: (id: number, engagedOnly: boolean) =>
    [...emailAccountKeys.all, "activity-day-summary", id, engagedOnly] as const,
  /** One day's threads, keyed by `dayOffset` — lazy (`enabled` gated on first
   * expand). "Show more" pages are appended into this same cache entry in
   * place (see `useLoadMoreActivityCyclesMutation`), not fetched under a
   * second key. `engagedOnly` (the Activity tab's engagement checkbox) is
   * part of the key so flipping it lands on a FRESH cache entry rather than
   * mutating an existing one — the old entry, accumulated "Show more" pages
   * and all, survives untouched for when the checkbox flips back. */
  activityCycles: (id: number, dayOffset: number, engagedOnly: boolean) =>
    [...emailAccountKeys.all, "activity-cycles", id, dayOffset, engagedOnly] as const,
  /** All 5 days' first page of threads in one fetch — fires once on mount,
   * seeds each day's own `activityCycles(id, dayOffset, engagedOnly)` cache
   * entry via `initialData` (see `useActivityCyclesQuery`) instead of each day
   * firing its own request. Keyed on `engagedOnly` for the same reason. */
  activityCyclesBulk: (id: number, engagedOnly: boolean) =>
    [...emailAccountKeys.all, "activity-cycles-bulk", id, engagedOnly] as const,
  /** "Recent Mailbox History" side panel, capped at `limit`. */
  mailboxHistory: (id: number, limit: number) =>
    [...emailAccountKeys.all, "mailbox-history", id, limit] as const,
};

/**
 * Preset SMTP/IMAP provider catalog (TRU-52). The data is a static, tenant-
 * agnostic config served from the API, so it effectively never goes stale
 * within a session — `staleTime: Infinity` avoids needless refetches when the
 * connect modal re-opens.
 */
export function useProviderPresetsQuery() {
  return useQuery<ProviderPreset[]>({
    queryKey: emailAccountKeys.providerPresets(),
    queryFn: () => getProviderPresets(),
    staleTime: Infinity,
  });
}

/**
 * Distinct workspace domains + per-domain account counts, for the filter
 * drawer's Domain dropdown (V1.1). Always enabled — cheap, workspace-scoped,
 * and useful the moment the drawer opens rather than only after some other
 * gate. `silentError` because a failed fetch should just leave the dropdown
 * on "All domains", not toast-spam a filter sub-control (see apps/web
 * CLAUDE.md's Toast section).
 */
export function useEmailAccountDomainsQuery(options: { enabled?: boolean } = {}) {
  return useQuery<EmailAccountDomain[]>({
    queryKey: emailAccountKeys.domains(),
    queryFn: () => listEmailAccountDomains(),
    meta: { silentError: true },
    enabled: options.enabled ?? true,
  });
}

/** Page size for the mailbox switcher — lazy loading fetches this many per scroll. */
export const MAILBOX_LOOKUP_PAGE_SIZE = 20;

/**
 * The mailbox-switcher list (TRU-1250) — paged, prefix-searchable.
 * `getNextPageParam` returns an offset only while the server says `hasMore`, so
 * `hasNextPage` alone drives lazy loading. `q` is in the key, so changing the
 * search starts fresh pages and an offset can't leak across searches.
 */
export function useMailboxLookupQuery(q: string, options: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: emailAccountKeys.lookup(q),
    queryFn: ({ pageParam }) =>
      lookupEmailAccounts({
        q,
        limit: MAILBOX_LOOKUP_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.reduce((n, p) => n + p.items.length, 0) : undefined,
    // An affordance, not the page's content — a failed lookup shows inline
    // rather than toasting over whatever the user is doing.
    meta: { silentError: true },
    enabled: options.enabled ?? true,
  });
}

/**
 * How long an unused page/filter/sort combination of the accounts list stays in
 * cache — 2 minutes instead of the 5-minute global default. Registered once as
 * a key-prefix default in `main.tsx` rather than passed to `useQuery`, because a
 * query's `gcTime` is the MAX across everything that touched it: the route
 * loader's `ensureQueryData` and the page-size seed's `setQueryData` would each
 * pin it back to 5 minutes.
 */
export const EMAIL_ACCOUNTS_LIST_GC_MS = 2 * 60_000;

/** Listing query. Loaders call the same `listAccounts` via `ensureQueryData`.
 *
 * The backend response is the full `PaginatedEmailAccounts` envelope
 * (`items` + `total` + `page` + `limit` + `counts`). `placeholderData: (prev)
 * => prev` keeps the previous page visible during a page/filter transition
 * so the table doesn't flash empty between fetches. */
export function useEmailAccountsListQuery(params: ListEmailAccountsParams) {
  const isPolling = useIsBulkDeletePolling();
  return useQuery({
    queryKey: emailAccountKeys.list(params),
    queryFn: () => listAccounts(params),
    placeholderData: (prev) => prev,
    refetchInterval: isPolling ? 15_000 : false,
  });
}

/** Single-account detail fetch. The overview query is preferred for the
 * detail page since it includes stats; this hook is here for callers that
 * only need the account shape. */
export function useEmailAccountDetailQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: emailAccountKeys.detail(id),
    queryFn: () => getEmailAccount(id),
    enabled,
  });
}

/**
 * How often the overview re-reads itself while a mailbox check is still out.
 *
 * The run takes hours (3 test emails per direction, the last at T+4h30), so
 * this is not "wait for the result" — it is what keeps the Setup &
 * authentication card's progress line and its Re-check cooldown honest on a
 * page someone leaves open. A minute is the resolution both of them render at.
 */
const CHECK_IN_PROGRESS_POLL_MS = 60_000;

/** Detail page's primary query — account + stats + latest verification run. */
export function useEmailAccountOverviewQuery(id: number) {
  return useQuery({
    queryKey: emailAccountKeys.overview(id),
    queryFn: () => getEmailAccountOverview(id),
    staleTime: 30_000,
    // Gated on the SERVER's own flag rather than on "a check was started from
    // this tab": a run kicked off at connect, by the daily cron, or in another
    // tab is just as much in progress, and the poll must stop on the response
    // that clears the flag — not on a local timer that can outlive it.
    refetchInterval: (query) =>
      query.state.data?.domainSecurity?.checkInProgress === true
        ? CHECK_IN_PROGRESS_POLL_MS
        : false,
  });
}

/**
 * The reputation chart's server-computed series.
 *
 * Its own query rather than a field on the overview: it is a separate endpoint
 * over a separate table, and folding them together would make one slow or
 * failing read blank the whole detail page. Kept separate, the chart can render
 * empty while every other card on the page is fine.
 *
 * `staleTime` is an hour because the cron behind `days` writes once a day — a
 * shorter window would refetch a series that provably cannot have changed.
 *
 * That reasoning now covers only `days`. The response's `rates` are computed
 * live per request, so they can move within the hour, and a `rates: null` from
 * a transient analytics outage stays cached (rendered as `—`) until the entry
 * goes stale. Left as-is deliberately: shortening the window would re-query the
 * whole endpoint for the two footer figures. Revisit if the `—` proves sticky
 * enough to confuse users.
 */
export function useEmailAccountScoresQuery(id: number, days = 30) {
  return useQuery({
    queryKey: emailAccountKeys.scores(id, days),
    queryFn: () => getEmailAccountScores(id, days),
    staleTime: 60 * 60_000,
  });
}

/**
 * Internal helper: when a mutation returns an updated EmailAccount, push it
 * into the list, detail, and overview caches in one call. Centralising this
 * keeps every account-mutating hook consistent — adding a new cache slot
 * later only requires updating one place.
 *
 * The list cache is the server-paginated `PaginatedEmailAccounts` shape, so
 * we splice into `items` (status-derived ordering is unchanged for in-place
 * updates). We deliberately do NOT recompute `counts` / `total` here — a
 * status change can move an account across buckets (e.g. RUNNING → PAUSED),
 * which would require us to recompute server-side filters client-side. We
 * invalidate `lists()` after the cache write so the next render refetches
 * authoritative counts; the in-place splice keeps the UI accurate in the
 * meantime.
 *
 * We also invalidate `onboardingKeys.statuses()` (prefix — fans out to every
 * cached workspace slot). The onboarding status is server-derived from
 * "≥1 account with status=CONNECTED AND a credentials row" and
 * "warmupState.warmupStatus=RUNNING > 0", so any account add/update/delete
 * or warmup enable/pause can flip `emailConnected` / `warmupEnabled` and
 * lazily set `completedAt`. Without this, a user who connects an SMTP
 * account and enables warmup in the same SPA session keeps seeing the
 * `OnboardingChecklist` (gated on `formCompleted && !completedAt`) until the
 * status cache goes stale on its own.
 */
function propagateAccountUpdate(
  qc: ReturnType<typeof useQueryClient>,
  account: EmailAccount,
): void {
  // Single-row patch, NOT `invalidateQueries(lists())` — the server already
  // handed us the updated row. This also kills the old pre-commit clobber:
  // nothing refetches the list, so an optimistic warmup snapshot stands until
  // `pollUntilWorkerConfirms` replaces it.
  patchAccountInLists(qc, emailAccountKeys.lists(), account);
  qc.setQueryData(emailAccountKeys.detail(account.id), account);
  qc.setQueryData<EmailAccountOverview>(
    emailAccountKeys.overview(account.id),
    (prev) => (prev ? { ...prev, account } : prev),
  );
  // The DWD Manage-Mailboxes rows carry the same warmup state, and must be
  // written from here: the warmup endpoints return the PRE-transition row, so
  // only `pollUntilWorkerConfirms`' later snapshot — which lands here — is real.
  patchDwdConnectedEmailWarmup(qc, account);
  void qc.invalidateQueries({ queryKey: onboardingKeys.statuses() });
  // A warmup-state change (Resume of a suspension, enable, pause) is exactly
  // what resolves or creates an Action item. Prefix key: every cached filter /
  // page variant refetches.
  void qc.invalidateQueries({ queryKey: emailAccountKeys.actionItems() });
}

/**
 * `senderName` is ONE column (`emailAccountWarmupConfig.senderName`) edited
 * from two screens — the Edit account (SMTP) modal and Warmup Settings — and
 * mirrored into several caches. Whichever side saves calls this so the other
 * never shows a stale value: patches the detail + overview copies (incl. the
 * overview's `domainSecurity.senderName`) and DWD Manage-Mailboxes rows, and
 * invalidates the OTHER editor's
 * seed query (`staleKey`). The saving editor's own cache is left alone — the
 * warmup form writes its response in with `setQueryData`, and re-fetching it
 * would re-seed a form the user just saved.
 */
function syncSenderNameCaches(
  qc: ReturnType<typeof useQueryClient>,
  id: number,
  senderName: string | null,
  staleKey: QueryKey,
): void {
  qc.setQueryData<EmailAccount>(emailAccountKeys.detail(id), (prev) =>
    prev ? { ...prev, senderName } : prev,
  );
  qc.setQueryData<EmailAccountOverview>(emailAccountKeys.overview(id), (prev) =>
    prev
      ? {
          ...prev,
          account: { ...prev.account, senderName },
          domainSecurity: prev.domainSecurity && { ...prev.domainSecurity, senderName },
        }
      : prev,
  );
  patchDwdConnectedEmailSenderName(qc, id, senderName);
  void qc.invalidateQueries({ queryKey: staleKey });
}

/**
 * Connect a new SMTP/IMAP account. Optimistic: prepend the returned account
 * onto the cached list as soon as the server confirms (we can't fabricate
 * server-assigned ids/timestamps, so we wait for the response — true
 * optimistic would need a temp-id reconciliation step).
 */
export function useAddSmtpAccountMutation() {
  const qc = useQueryClient();

  return useMutation({
    // No `silentError`: the connect form has no inline error surface any more,
    // so a thrown request is auto-toasted by `MutationCache.onError`.
    meta: { successMessage: 'Account connected' },
    mutationFn: (payload: ConnectSmtpPayload) => connectSmtp(payload),
    onSuccess: (account) => {
      // Seed the per-id cache so a detail route opening immediately after
      // doesn't have to refetch.
      qc.setQueryData(emailAccountKeys.detail(account.id), account);
      // The list is server-paginated — a prepend would corrupt page
      // boundaries and `counts`. Invalidate so every cached page/filter
      // variant refetches authoritative data. `placeholderData: prev` on
      // the list query keeps the previous data visible meanwhile.
      void qc.invalidateQueries({ queryKey: emailAccountKeys.lists() });
      // Onboarding status's `emailConnected` is derived from "≥1 account
      // with status=CONNECTED AND a credentials row in this workspace";
      // adding a freshly-connected SMTP account flips it true and may lazily
      // set `completedAt` on the next status fetch. Without busting this
      // cache, the onboarding checklist would keep showing "form done,
      // accounts not connected" after the user actually connected one. We
      // invalidate by prefix so every cached workspace slot refreshes —
      // necessary because the `setQueryData` for the detail/list above is
      // scoped to whatever workspace this mutation ran under.
      void qc.invalidateQueries({ queryKey: onboardingKeys.statuses() });
    },
  });
}

/**
 * The reverse, human-initiated Saleshandy linking flow (`POST
 * /saleshandy/link/verify`) — see `lib/saleshandy.ts` and the
 * `saleshandy-link` catalog entry's panel, `SaleshandyConnectPanel.tsx`
 * (surfaced from the "Connect an email account" page — moved here from
 * Settings → Organization, revision 18: this is where a user actually goes
 * looking to add a mailbox). `meta: silentError` because the panel owns its
 * own inline success/error UI (including a non-error treatment for the 409
 * "already linked" outcome), not a toast.
 *
 * Unlike every other mutation in this file, a successful link doesn't touch
 * the email-account list directly — mailboxes arrive later via the backend's
 * own sync (the daily pull cron, plus a one-shot right after this call
 * succeeds server-side). What DOES change immediately is the org's plan (the
 * sync step inside `retroactiveLink` can assign a new one) — invalidate both
 * the org record and the whole billing surface, mirroring how a plan change
 * from any other path (checkout, admin override) is picked up.
 */
export function useLinkSaleshandyAccountMutation() {
  const qc = useQueryClient();
  return useMutation<SaleshandyRetroactiveLinkResult, Error, string>({
    meta: { silentError: true },
    mutationFn: linkSaleshandyAccount,
    onSuccess: () => {
      // The link is created unconsented, so nothing about the plan has changed
      // yet — it syncs when the owner confirms on Saleshandy. Refresh the
      // status so a return to this page reflects whatever has happened since.
      void qc.invalidateQueries({ queryKey: saleshandyLinkStatusKey });
      void qc.invalidateQueries({ queryKey: orgKeys.current() });
      void qc.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

const saleshandyLinkStatusKey = ['saleshandy', 'link-status'] as const;

/**
 * Whether the org already has a consented Saleshandy link, so the connect panel
 * can show "already connected" instead of an empty key field. `silentError`:
 * the panel falls back to the key form, which the server still guards.
 */
export function useSaleshandyLinkStatusQuery() {
  return useQuery<SaleshandyLinkStatus>({
    queryKey: saleshandyLinkStatusKey,
    queryFn: getSaleshandyLinkStatus,
    meta: { silentError: true },
  });
}

/**
 * Fetch an existing account's SMTP/IMAP server settings for the edit form.
 * `enabled: id != null` keeps the query dormant in add mode (where the form is
 * mounted without an `accountId`).
 *
 * Keeps `meta.silentError` — unlike the SMTP mutations, this one still owns an
 * inline surface: `SmtpConnectForm` renders a "Couldn't load account settings"
 * banner with a **Retry** button in place of the form body. That's a failed data
 * fetch with an action, not an ephemeral request error, and a toast can't hold a
 * button — so it stays inline and must not double-report as a pill.
 */
export function useSmtpConfigQuery(id?: number) {
  const qc = useQueryClient();
  return useQuery<SmtpConfig>({
    queryKey: emailAccountKeys.smtpConfig(id ?? 0),
    queryFn: () => getSmtpConfig(id!),
    enabled: id != null,
    // The response carries the decrypted passwords, so it must not outlive the
    // edit form: with `gcTime: 0` the entry is dropped as soon as the form
    // unmounts (popup closed) instead of lingering for the default 5 minutes.
    gcTime: 0,
    // `023001` is deterministic (the ciphertext won't decrypt on a retry), and
    // the form has a dedicated re-enter-the-password state for it.
    // Every other error defers to the client's default policy.
    retry: (failureCount, err) => {
      if (isStoredPasswordUnreadable(err)) return false;
      const base = qc.getDefaultOptions().queries?.retry ?? 3;
      if (typeof base === 'function') return base(failureCount, err);
      return typeof base === 'number' ? failureCount < base : base;
    },
    meta: { silentError: true },
  });
}

/**
 * Update an existing SMTP/IMAP account's server settings. The response is the
 * full updated `EmailAccount` (same shape `connectSmtp` returns), so we
 * propagate it into the detail cache and invalidate the lists + onboarding
 * status exactly like `useAddSmtpAccountMutation` — a server-settings edit
 * can flip connection status, which the onboarding `emailConnected` signal is
 * derived from.
 */
export function useUpdateSmtpAccountMutation() {
  const qc = useQueryClient();

  return useMutation({
    // No `silentError`: see `useAddSmtpAccountMutation` — the form's inline
    // error block is gone, so the cache hook owns the failure toast.
    meta: { successMessage: 'Account updated' },
    mutationFn: ({ id, payload }: { id: number; payload: UpdateSmtpPayload }) =>
      updateSmtp(id, payload),
    onSuccess: (account) => {
      propagateAccountUpdate(qc, account);
      syncSenderNameCaches(
        qc,
        account.id,
        account.senderName,
        emailAccountKeys.warmupSettings(account.id),
      );
    },
  });
}

/**
 * Reconnect a disconnected account. The response is a union — an OAuth
 * account returns `{ redirectUrl }` (nothing to cache; the caller navigates
 * the browser there), a PASSWORD account returns the full updated
 * `EmailAccount` (propagated into every cache slot + a success toast,
 * mirroring `useUpdateSmtpAccountMutation`). No `meta.successMessage`: the
 * toast only fires on the account-shape branch, so it's an inline
 * `notifySuccess` call here instead of the global meta pipe — still exactly
 * one mechanism per mutation, just conditional on which branch resolved.
 * No `silentError` either — `Reconnect.AlreadyConnected`,
 * `Reconnect.MissingSmtpFields`, and any probe failure should surface via
 * the standard auto-toast error pipe, not be swallowed.
 *
 * Uses `propagateAccountUpdate` (not a hand-rolled cache write) — it already
 * invalidates `onboardingKeys.statuses()` internally, so no separate
 * onboarding invalidation is needed here.
 */
export function useReconnectAccountMutation() {
  const qc = useQueryClient();
  return useMutation<
    EmailAccount | { redirectUrl: string },
    Error,
    { id: number; payload?: ReconnectSmtpPayload }
  >({
    mutationFn: ({ id, payload }) => reconnectAccount(id, payload),
    onSuccess: (result) => {
      if ('redirectUrl' in result) return;
      propagateAccountUpdate(qc, result);
      syncSenderNameCaches(
        qc,
        result.id,
        result.senderName,
        emailAccountKeys.warmupSettings(result.id),
      );
      notifySuccess('Account reconnected');
    },
  });
}

/**
 * Validate SMTP/IMAP creds without persisting. Not optimistic — the result is
 * the entire point of calling it.
 *
 * Two distinct failure channels, and only one of them lands here. A failed
 * PROBE is a **200 with a negative body** (`{ smtp: { isConnected: false, cause,
 * message } }`), so it resolves as a success — `useSmtpForm.handleTest` inspects
 * the body and hand-fires the toast per failing leg. This mutation's error path
 * is only for a genuinely thrown request (network, or the 400
 * `TestSmtp.HostNotAllowed` / `021103`); no `silentError`, so the cache hook
 * toasts it.
 */
export function useTestSmtpConnectionMutation() {
  return useMutation<SmtpTestResult, Error, TestSmtpPayload>({
    mutationFn: (payload) => testSmtpConnection(payload),
  });
}

const PAUSE_POLL_DELAY_MS = 1500;
const PAUSE_POLL_MAX_ATTEMPTS = 6;

/**
 * Poll cadence: every ~1.5s for up to ~9s total (comfortably above the
 * worker's p99 commit latency on a healthy queue).
 *
 * Bounded short-poll for warmup state-change worker confirmation, shared by
 * enable/pause/resume. The `POST /warmup/{enable,pause,resume}` endpoints only
 * enqueue a `WARMUP_ACCOUNT_EVENT` and return an optimistic (or still-stale)
 * snapshot before the warmup-worker commits the transition. So each caller
 * writes its optimistic snapshot, then runs this poll: every ~1.5s for up to
 * ~9s it re-fetches THAT ONE ACCOUNT (`GET /email-accounts/:id`) and patches
 * the row into every cache slot, until the mutation-specific `isCommitted`
 * predicate holds. On cap-hit without confirmation we warn; the natural
 * refetch path (window focus, stale time) reconciles eventually.
 *
 * `isCommitted` only decides when to STOP polling — every iteration propagates
 * identically now that nothing invalidates the list.
 */
async function pollUntilWorkerConfirms(
  qc: ReturnType<typeof useQueryClient>,
  id: number,
  isCommitted: (a: EmailAccount) => boolean,
  slowLabel: string,
): Promise<void> {
  for (let i = 0; i < PAUSE_POLL_MAX_ATTEMPTS; i++) {
    await new Promise((resolve) => setTimeout(resolve, PAUSE_POLL_DELAY_MS));
    let fresh: EmailAccount;
    try {
      fresh = await getEmailAccount(id);
    } catch {
      // Transient fetch failure — TanStack Query's natural refetch path will
      // reconcile. Bail out quietly; surfacing every blip would be noise.
      return;
    }
    propagateAccountUpdate(qc, fresh);
    if (isCommitted(fresh)) return;
  }
  notifyWarning(
    `${slowLabel} is taking longer than usual. The worker may be backed up. The UI will update once it catches up.`,
  );
}

/**
 * `silentError` so the detail action bar can branch on `err.errorCode`
 * (`Suspended` / `Disconnected` / `MailboxVerificationPending` /
 * `MailboxVerificationFailed`) inline — mirrors `useResumeWarmupMutation`.
 * The handler owns a generic fallback for any other code too, so this must
 * stay paired with that full coverage; dropping `silentError` here without
 * also removing the handler's branches double-toasts (the global auto-toast
 * AND the inline one fire for the same error).
 */
export function useEnableWarmupMutation() {
  const qc = useQueryClient();
  return useMutation<EmailAccount, Error, number>({
    meta: { silentError: true, successMessage: "Warmup enabled" },
    mutationFn: (id) => enableWarmup(id),
    onSuccess: (account, id) => {
      // Optimistic write only — do NOT eagerly invalidate lists: the worker
      // commits the transition asynchronously, so a pre-commit list refetch
      // would clobber this optimistic ACTIVE back to the old status. The
      // detached confirmation poll reconciles (and does the single committed
      // list invalidate). Fire-and-forget so the mutation settles immediately
      // and the toggle re-enables (same rationale as pause/resume).
      propagateAccountUpdate(qc, account);
      // The enable response is the optimistic `active` snapshot (the API patches
      // `accountState` in-memory; the worker commits later). Run the
      // confirmation poll to fetch the worker-committed row and reconcile the
      // server-derived list counts once it lands. Gate on the optimistic bucket
      // actually being `active`: a degraded response that didn't flip to active
      // has nothing to confirm, so skip the poll rather than spin for 9s.
      if (accountStatusBucket(account) === "active") {
        void (async () => {
          await pollUntilWorkerConfirms(
            qc,
            id,
            (a) => accountStatusBucket(a) === "active",
            "Enable",
          );
        })();
      }
    },
  });
}

export function usePauseWarmupMutation() {
  const qc = useQueryClient();
  return useMutation<EmailAccount, Error, number>({
    meta: { successMessage: "Warmup paused" },
    mutationFn: (id) => pauseWarmup(id),
    onSuccess: (account, id) => {
      // The worker-confirmation poll runs for up to ~9s. It is a *background*
      // cache-reconciliation task, not part of the pause request itself, so we
      // deliberately do NOT `await` it: awaiting keeps the mutation `isPending`
      // for the whole poll window, which leaves the toolbar's Pause button
      // stuck/disabled showing "Pausing…" — and blocks a follow-up Resume from
      // re-enabling it — until a full page refresh discards the pending
      // mutation. Fire-and-forget so the mutation settles as soon as the server
      // responds; the poll keeps writing fresh snapshots into the cache as they
      // arrive (see `propagateAccountUpdate`).
      if (account.warmupStatus === "PAUSED" && !account.warmupPausedAt) {
        // Optimistic-only write — do NOT eagerly invalidate lists: a pre-commit
        // list refetch reads the still-RUNNING DB row and clobbers this PAUSED
        // optimistic value. The poll does the committed list invalidate.
        propagateAccountUpdate(qc, account);
        void (async () => {
          await pollUntilWorkerConfirms(
            qc,
            id,
            (a) => Boolean(a.warmupPausedAt),
            "Pause",
          );
        })();
      } else {
        // The worker had already committed (`warmupPausedAt` populated) by the
        // time the API re-read — safe to reconcile the list counts now.
        propagateAccountUpdate(qc, account);
      }
    },
  });
}

/**
 * Resume warmup for a paused or suspended account. Mirrors
 * `usePauseWarmupMutation`: propagate the returned account into every cache
 * slot, then fire-and-forget a worker-confirmation poll (not awaited, so the
 * mutation settles as soon as the server responds and the Resume button
 * re-enables) when the optimistic snapshot is still in a paused/suspended
 * bucket. `silentError` so the detail action bar can branch on `err.errorCode`
 * (`ResumeWarmup.NotSuspended` `022601`) inline; the success toast is static.
 */
export function useResumeWarmupMutation() {
  const qc = useQueryClient();
  return useMutation<EmailAccount, Error, number>({
    meta: { silentError: true, successMessage: "Warmup resumed" },
    mutationFn: (id) => resumeWarmup(id),
    onSuccess: (account, id) => {
      const bucket = accountStatusBucket(account);
      if (bucket === "paused" || isSuspendedBucket(bucket)) {
        // `resumeWarmup` returns the still-paused/suspended snapshot by design
        // (the worker owns the transition). Optimistic-only write; the poll
        // does the committed list invalidate once the worker flips the bucket.
        propagateAccountUpdate(qc, account);
        void pollUntilWorkerConfirms(
          qc,
          id,
          (a) => {
            const b = accountStatusBucket(a);
            return b !== "paused" && !isSuspendedBucket(b);
          },
          "Resume",
        );
      } else {
        // Already out of paused/suspended on the response — safe to reconcile
        // the list counts now.
        propagateAccountUpdate(qc, account);
      }
    },
  });
}

export function useDisconnectAccountMutation() {
  const qc = useQueryClient();
  return useMutation<EmailAccount, Error, number>({
    meta: { successMessage: "Account disconnected" },
    mutationFn: (id) => disconnectAccount(id),
    onSuccess: (account) => {
      propagateAccountUpdate(qc, account);
    },
  });
}

export function useDeleteEmailAccountMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    meta: { successMessage: "Email account deleted" },
    mutationFn: (id) => deleteEmailAccount(id),
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: emailAccountKeys.detail(id) });
      qc.removeQueries({ queryKey: emailAccountKeys.overview(id) });
      // Page boundaries shift on delete — splicing from a server-paginated
      // list would leave the current page short and the next page
      // stale. Invalidate every cached list variant.
      void qc.invalidateQueries({ queryKey: emailAccountKeys.lists() });
      void qc.invalidateQueries({ queryKey: emailAccountKeys.actionItems() });
      // Deleting the last account flips `emailConnected` (and transitively
      // `warmupEnabled`) back to false; refetch the onboarding status so any
      // checklist / hero UI reflects the post-delete reality.
      void qc.invalidateQueries({ queryKey: onboardingKeys.statuses() });
    },
  });
}

/**
 * Re-run DNS checks. The endpoint re-resolves, re-persists, and returns the
 * refreshed domain-security snapshot (record values + per-field bands + score)
 * — not an account object.
 *
 * The fresh snapshot is spliced straight into the overview cache so the
 * detail page repaints without a refetch. The accounts list is invalidated
 * rather than patched: a re-check also moves the account's health-derived
 * columns, and those are computed server-side per row.
 */
/**
 * The two ways the server refuses a manual Re-check: inside the 3h window
 * (`023601`) or while a test-email run is still out (`023602`). Neither is an
 * error the user made — the card already renders both as a disabled button, and
 * a red toast on top of that would be the UI arguing with itself.
 *
 * Branches on the catalog code, never on the message.
 */
function isRecheckRefused(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.errorCode === ErrorCode.EmailAccounts.CheckDns.RecheckTooSoon ||
      err.errorCode === ErrorCode.EmailAccounts.CheckDns.CheckInProgress)
  );
}

export function useCheckDnsMutation(id: number) {
  const qc = useQueryClient();
  return useMutation<DomainSecurity | null, Error, void>({
    // `silentError` so the two refusal codes below can be swallowed; this hook's
    // own `onError` is therefore the ONLY feedback for every other failure, and
    // each branch must end in one (same contract as `useEnableWarmupMutation`).
    meta: { silentError: true, successMessage: "DNS records refreshed" },
    mutationFn: () => checkDns(id),
    onError: (err) => {
      if (isRecheckRefused(err)) {
        // The click was refused because this tab's snapshot was stale — it
        // thought Re-check was allowed. Re-read the overview so the button
        // picks up the server's real `nextManualCheckAt` / `checkInProgress`
        // and disables itself with the countdown, instead of staying clickable
        // and refusable.
        void qc.invalidateQueries({ queryKey: emailAccountKeys.overview(id) });
        return;
      }
      notifyApiError(err, "Couldn't re-check this mailbox. Please try again.");
    },
    onSuccess: (domainSecurity) => {
      // Paint the fresh evidence immediately — but only when there IS any. A
      // null response means the refresh produced no snapshot; writing it over a
      // previously-good payload would blank the card on a transient failure.
      if (domainSecurity) {
        qc.setQueryData<EmailAccountOverview>(emailAccountKeys.overview(id), (prev) =>
          prev ? { ...prev, domainSecurity } : prev,
        );
      }
      // Then refetch the overview anyway: the same request also rewrote the four
      // DNS record objects (`spfData`/`dkimData`/`dmarcData`/`mxRecordData`),
      // which reach the page through `overview.account` — the status panel, the
      // header banner, and the warmup-enable gate (`warmupEnableBlockedReason` →
      // `dnsUnverifiedRecords`) all read them. Patching only `domainSecurity` left the checklist green
      // while the header still called the account DNS-blocked and the enable
      // toggle stayed disabled until you navigated away.
      void qc.invalidateQueries({ queryKey: emailAccountKeys.overview(id) });
      // A fixed record can clear (or a still-broken one add) a DNS Action item.
      void qc.invalidateQueries({ queryKey: emailAccountKeys.actionItems() });
      // The recheck moves this row's health-derived list columns, so re-read
      // just that row instead of invalidating the table. Fire-and-forget: a
      // failed re-read must not turn a successful recheck into an error toast.
      void getEmailAccount(id)
        .then((account) => patchAccountInLists(qc, emailAccountKeys.lists(), account))
        .catch(() => undefined);
    },
  });
}

export function useWarmupSettingsQuery(id: number | null) {
  return useQuery<WarmupSettings>({
    queryKey: emailAccountKeys.warmupSettings(id ?? 0),
    queryFn: () => getWarmupSettings(id!),
    enabled: id != null,
  });
}

// ── Email Account Activity tab (v2) ──────────────────────────────────────────

/**
 * The 5-day activity day-summary window (counts only, no items). One
 * `useQuery`, fires on mount. `engagedOnly` defaults to `true` — the Activity
 * tab's own default — so `MailboxHistoryPanel`, which only reads `timezone`,
 * shares the tab's initial fetch. Unlike v1's combined feed query,
 * this is cheap/lightweight (counts only) — the accumulated "Show more"
 * state v1's `staleTime: Infinity` existed to protect now lives in the
 * separate per-day `useActivityCyclesQuery` cache entries, so this query uses
 * the normal global default (`query-client.ts`).
 */
export function useActivityDaySummaryQuery(
  id: number,
  engagedOnly = true,
  options: { enabled?: boolean } = {},
) {
  return useQuery<ActivityDaySummaryResponse>({
    queryKey: emailAccountKeys.activityDaySummary(id, engagedOnly),
    queryFn: () => getActivityDaySummary(id, engagedOnly),
    enabled: options.enabled,
  });
}

/**
 * All 5 days' first page of threads in one fetch — fires once on mount, same
 * shape as `useActivityDaySummaryQuery`. `ActivityFeed` reads `data.days` to
 * seed each day's own `useActivityCyclesQuery` via `initialData` so the 5
 * per-day queries never fire their own network request on initial load.
 *
 * `engagedOnly` is a query-KEY input, not a refetch trigger: toggling the
 * checkbox selects a different cache entry (fetching it the first time,
 * serving it from cache afterwards), which is why toggling back is instant
 * and cannot clobber the other filter's accumulated pages.
 */
export function useActivityCyclesBulkQuery(id: number, engagedOnly: boolean) {
  return useQuery<ActivityCyclesBulkResponse>({
    queryKey: emailAccountKeys.activityCyclesBulk(id, engagedOnly),
    queryFn: () => getActivityCyclesBulk(id, engagedOnly),
  });
}

/**
 * One day's threads — **lazy**: `enabled` is caller-controlled
 * (`ActivityDaySection` passes its own expand/collapse state) so nothing
 * fetches until the day is first expanded, the single most important
 * behavior change from v1 (which fetched all 5 days' items eagerly on
 * mount). On first expand this fetches page 1 (no `lastEventAt`); "Show
 * more" is a separate imperative continuation (`useLoadMoreActivityCyclesMutation`
 * below), not a refetch of this query.
 *
 * `options.initialCycles`, when provided, seeds this query's cache entry via
 * TanStack Query's `initialData` — synchronous at query-creation time, so it
 * has no dependency on effect-commit order across component boundaries (a
 * `useEffect`-based priming pass in the parent was considered and rejected:
 * React commits a child's effects before its parent's within the same
 * commit, so priming from `ActivityFeed`'s effect would let each
 * `ActivityDaySection`'s own query observer dispatch a real fetch against a
 * still-empty cache before the parent's priming effect ever ran). This is
 * what lets `ActivityFeed`'s single bulk fetch replace the 5 separate
 * per-day requests this query used to always issue on first expand.
 *
 * `staleTime: Infinity` is load-bearing here (mirrors v1's rationale on the
 * old combined feed query, now narrowed to just this per-day cache entry): a
 * background refetch under the global default would replace this cache
 * entry — including every appended "Show more" page — with the bare page-1
 * response, which is exactly the reset the append contract forbids. The plan
 * also lists "No real-time/live updates" as an explicit non-goal, so there's
 * no competing need for a background refetch here.
 */
export function useActivityCyclesQuery(
  id: number,
  dayOffset: number,
  engagedOnly: boolean,
  options: { enabled: boolean; initialCycles?: ActivityCyclesResponse },
) {
  return useQuery<ActivityCyclesResponse>({
    queryKey: emailAccountKeys.activityCycles(id, dayOffset, engagedOnly),
    queryFn: () => getActivityCycles(id, { dayOffset, engagedOnly, limit: ACTIVITY_CYCLES_INITIAL_LIMIT }),
    enabled: options.enabled,
    staleTime: Infinity,
    ...(options.initialCycles && { initialData: options.initialCycles, initialDataUpdatedAt: Date.now() }),
  });
}

/**
 * "Show more" within one already-expanded day: an imperative fetch (not a
 * second `useQuery`) that re-fetches with `lastEventAt` = the previous
 * response's cursor and appends the new page's `threads` onto the
 * `useActivityCyclesQuery` cache entry for the same
 * `(id, dayOffset, engagedOnly)` — cumulative, never a replace. Mirrors v1's
 * per-day "Show more" cache-append discipline (`qc.setQueryData` reading
 * current state then spreading), just keyed by `dayOffset` + cursor instead
 * of `date` + numeric `offset`. `engagedOnly` must match the query's own, or
 * the cursor walks a different list than the one on screen.
 */
export function useLoadMoreActivityCyclesMutation(
  id: number,
  dayOffset: number,
  engagedOnly: boolean,
) {
  const qc = useQueryClient();
  return useMutation<ActivityCyclesResponse, Error, void>({
    // `ActivityDaySection` renders the failure inline with a Retry.
    meta: { silentError: true },
    mutationFn: async () => {
      const cached = qc.getQueryData<ActivityCyclesResponse>(
        emailAccountKeys.activityCycles(id, dayOffset, engagedOnly),
      );
      return getActivityCycles(id, {
        dayOffset,
        engagedOnly,
        limit: ACTIVITY_CYCLES_LIMIT,
        lastEventAt: cached?.lastEventAt ?? undefined,
        lastHistoryId: cached?.lastHistoryId ?? undefined,
      });
    },
    onSuccess: (next) => {
      qc.setQueryData<ActivityCyclesResponse>(
        emailAccountKeys.activityCycles(id, dayOffset, engagedOnly),
        (prev) => (prev ? { ...next, threads: [...prev.threads, ...next.threads] } : next),
      );
    },
  });
}

/** "Recent Mailbox History" side panel — a bounded, non-paginated list. */
export function useMailboxHistoryQuery(id: number, limit: number = MAILBOX_HISTORY_LIMIT) {
  return useQuery<MailboxHistoryResponse>({
    queryKey: emailAccountKeys.mailboxHistory(id, limit),
    queryFn: () => getMailboxHistory(id, limit),
  });
}

/**
 * Enqueue an async bulk operation (`POST /bulk/jobs`). Returns `{ jobId, total }`
 * — the docked `BulkJobProgressPanel` then polls `useBulkJobStatusQuery` and
 * owns all progress + per-account-failure feedback, so this mutation is
 * `silentError`: the *enqueue-time* HTTP errors it surfaces (SelectionTooLarge
 * `023203`, EmptySelection `023202`) are rendered inline by the action bar via
 * `err.errorCode`, not auto-toasted (one mechanism, xor with the panel). The
 * caller writes the returned descriptor into `bulk-job-store` to open the panel;
 * list/onboarding invalidation happens when the job reaches a terminal status
 * (see `useBulkJobStatusQuery`), not here — the job hasn't run yet.
 */
export function useStartBulkOperationMutation() {
  return useMutation<BulkJobResponse, Error, StartBulkOperationBody>({
    meta: { silentError: true },
    mutationFn: (body) => startBulkOperation(body),
  });
}

/**
 * Read an async bulk job's status: one seed `GET .../bulk/jobs/:jobId` the
 * moment `jobId` exists, then live updates from Firestore from then on — no
 * repeated polling. The backend awaits its Firestore write before the
 * create-job response returns (`BulkJobService.createJob`), so the doc is
 * guaranteed to exist by the time we have a `jobId`; the Firebase listener
 * opens immediately, not gated on the seed GET landing. `notFound` is `true`
 * if either the seed GET 404s (`023204`, already expired at load time) or
 * Firestore later reports the doc gone (expired while being watched) — see
 * `BulkJobProgressPanel`. The panel owns the terminal cache-invalidation (it
 * knows the operation, so it can also bust the tag catalog for
 * add-/remove-tags).
 */
export function useBulkJobStatusQuery(jobId: string | null) {
  const uid = useAuthStore((s) => s.user?.id);

  const isFirebaseConfigured = Boolean(
    env.firebaseProjectId && env.firebaseApiKey,
  );

  const restQuery = useQuery<BulkJobStatus>({
    queryKey: emailAccountKeys.bulkJob(jobId ?? ""),
    queryFn: () => getBulkJobStatus(jobId!),
    enabled: jobId != null,
    meta: { silentError: true },
    // One-shot seed fetch only — no refetchInterval. Firestore is the sole
    // source of live updates after this; guard against incidental refetches
    // too so it truly only ever fires once per jobId.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Latch the terminal snapshot and stop the Firebase listener once reached
  // — the backend never writes to this doc again after markComplete, so
  // there's nothing further to listen for. Reset the latch whenever jobId
  // itself changes (id switch, not just a status change).
  const [latchedId, setLatchedId] = useState<string | null>(null);
  const [latched, setLatched] = useState<BulkJobStatus | undefined>(undefined);
  if (latchedId !== jobId) {
    setLatchedId(jobId);
    setLatched(undefined);
  }

  const {
    data: fbData,
    isConnectionPending,
    notFound: fbNotFound,
  } = useFirebaseData(
    uid,
    jobId ?? undefined,
    FIREBASE_BULK_JOBS_COLLECTION,
    isFirebaseConfigured && jobId != null && !latched,
  );

  const liveData = (fbData as unknown as BulkJobStatus | null) ?? restQuery.data;

  if (
    !latched &&
    liveData &&
    (liveData.status === "complete" || liveData.status === "failed")
  ) {
    setLatched(liveData);
  }

  const data = latched ?? liveData;

  const restNotFound =
    restQuery.error instanceof ApiError &&
    restQuery.error.errorCode === ErrorCode.EmailAccounts.BulkOps.JobNotFound;

  return {
    data,
    notFound: restNotFound || fbNotFound,
    isLoading: restQuery.isLoading && isConnectionPending && data == null,
    isFirebaseConfigured,
  };
}

/**
 * Upload a CSV to bulk-connect accounts. The created accounts exist immediately
 * (status DISCONNECTED) and flip to CONNECTED as the worker tests each one, so
 * we invalidate the list + onboarding status on the synchronous response.
 * `BulkCsvForm` renders the queued/invalid breakdown and the polled per-row
 * results inline, so the global error toast is suppressed.
 */
export function useBulkUploadCsvMutation() {
  const qc = useQueryClient();
  return useMutation<BulkConnectResult, Error, File>({
    meta: { silentError: true },
    mutationFn: (file) => uploadBulkCsv(file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: emailAccountKeys.lists() });
      void qc.invalidateQueries({ queryKey: onboardingKeys.statuses() });
    },
  });
}

/**
 * Read a bulk import's per-row connection results: one seed
 * `GET .../bulk-connect/:importId` the moment `importId` exists, then live
 * updates from Firestore from then on — no repeated polling. The backend
 * awaits its Firestore write before the upload response returns
 * (`EmailAccountsService.connectBulkEmailAccount`), so the doc is guaranteed
 * to exist by the time we have an `importId`; the Firebase listener opens
 * immediately, not gated on the seed GET landing.
 */
export function useBulkImportStatusQuery(importId: string | null) {
  const uid = useAuthStore((s) => s.user?.id);

  const isFirebaseConfigured = Boolean(
    env.firebaseProjectId && env.firebaseApiKey,
  );

  const restQuery = useQuery<BulkImportStatus>({
    queryKey: emailAccountKeys.bulkImport(importId ?? ""),
    queryFn: () => getBulkImportStatus(importId!),
    enabled: importId != null,
    meta: { silentError: true },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Latch the terminal snapshot and stop the Firebase listener once reached
  // — the backend never writes to this doc again once complete. Reset the
  // latch whenever importId itself changes.
  const [latchedId, setLatchedId] = useState<string | null>(null);
  const [latched, setLatched] = useState<BulkImportStatus | undefined>(undefined);
  if (latchedId !== importId) {
    setLatchedId(importId);
    setLatched(undefined);
  }

  const {
    data: fbData,
    isConnectionPending,
    notFound,
  } = useFirebaseData(
    uid,
    importId ?? undefined,
    FIREBASE_BULK_IMPORTS_COLLECTION,
    isFirebaseConfigured && importId != null && !latched,
  );

  const liveData = (fbData as unknown as BulkImportStatus | null) ?? restQuery.data;

  if (!latched && liveData && liveData.status === "complete") {
    setLatched(liveData);
  }

  const data = latched ?? liveData;

  return {
    data,
    notFound,
    isLoading: restQuery.isLoading && isConnectionPending && data == null,
    isFirebaseConfigured,
  };
}

// ── Tags ──────────────────────────────────────────────────────────────────────

/**
 * Workspace tag catalog (with usage counts) for the inline editor, the bulk
 * modal, and the tags filter. Shared across those surfaces under one key so a
 * single fetch backs them all; assignment mutations invalidate it so usage
 * counts and newly-created tags appear everywhere.
 */
export function useWorkspaceTagsQuery(options: { enabled?: boolean } = {}) {
  return useQuery<WorkspaceTag[]>({
    queryKey: emailAccountKeys.tags(),
    queryFn: () => listTags(),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

/**
 * Replace one account's full tag set. The response is the updated
 * `EmailAccount`, so we splice it into every account cache slot
 * (`propagateAccountUpdate`) and invalidate the tag catalog — a create-on-assign
 * adds a catalog row, and an orphan-cleanup removes one.
 */
export function useSetAccountTagsMutation() {
  const qc = useQueryClient();
  return useMutation<EmailAccount, Error, { id: number; names: string[] }>({
    // The inline editor / bulk modal own their inline UI; the propagated account
    // update is what the user sees. Surface a generic toast on failure.
    mutationFn: ({ id, names }) => setAccountTags(id, names),
    onSuccess: (account) => {
      propagateAccountUpdate(qc, account);
      void qc.invalidateQueries({ queryKey: emailAccountKeys.tags() });
    },
  });
}

export function useUpdateWarmupSettingsMutation(id: number) {
  const qc = useQueryClient();
  return useMutation<WarmupSettings, Error, UpdateWarmupSettingsPayload>({
    // The callsite's `onError` already fires a code-branched, friendlier
    // `toast.error` (see `use-warmup-settings-form.ts`). Keep `silentError`
    // so the MutationCache's auto-toast doesn't ALSO fire on the same failure
    // with the server's raw message — that would double-toast.
    meta: { silentError: true, successMessage: 'Warmup settings saved' },
    mutationFn: (payload) => updateWarmupSettings(id, payload),
    onSuccess: (data) => {
      qc.setQueryData(emailAccountKeys.warmupSettings(id), data);
      // The list table caches its own copy of industry/topics
      // (`contentIndustry`/`contentTopics`) per row — patch every cached list
      // page/filter variant in place rather than invalidating, so the table
      // reflects the save immediately with no extra `/email-accounts` request.
      qc.setQueriesData<PaginatedEmailAccounts>(
        { queryKey: emailAccountKeys.lists() },
        (prev) =>
          prev && {
            ...prev,
            items: prev.items.map((row) =>
              row.id === id
                ? {
                    ...row,
                    contentIndustry: data.industry ?? null,
                    contentTopics: data.topics ?? null,
                    senderName: data.senderName,
                  }
                : row,
            ),
          },
      );
      syncSenderNameCaches(qc, id, data.senderName, emailAccountKeys.smtpConfig(id));
    },
  });
}

// ── Column preferences (TRU-342) ────────────────────────────────────────────

/**
 * Query-key factory for table-column preferences. **Excludes** `workspaceId` —
 * the JWT carries the workspace scope, and the workspace-switch mutation's
 * unfiltered `qc.invalidateQueries()` (per `apps/web/CLAUDE.md` tenant-switch
 * rule) refetches this under the new workspace. Keying by `workspaceId` here
 * would leave a stale per-workspace slot live after a switch.
 */
export const columnPreferenceKeys = {
  all: ["preferences", "columns"] as const,
  columns: (tableKey: ColumnTableKey) =>
    [...columnPreferenceKeys.all, tableKey] as const,
};

/**
 * Query options for one table's column preference. Shared by the route loader's
 * `ensureQueryData` prime (flash-free first paint) and the table's
 * `useColumnPreferenceQuery`. A read error is treated as "no saved pref" by the
 * consumer (falls back to registry defaults) — it must never block the table,
 * so the error is auto-toasted (no `silentError`) but the UI degrades to
 * defaults via `normalizeColumnOrder(null)`.
 */
export function columnPreferenceQueryOptions(tableKey: ColumnTableKey) {
  return {
    queryKey: columnPreferenceKeys.columns(tableKey),
    queryFn: () => getColumnPreference(tableKey),
    staleTime: 5 * 60_000,
  };
}

export function useColumnPreferenceQuery(tableKey: ColumnTableKey) {
  return useQuery<ColumnPreference>(columnPreferenceQueryOptions(tableKey));
}

/**
 * Persist a table's ordered visible-column id list. Optimistic: write the new
 * order into the cache immediately, then invalidate so the server's stored
 * (normalized-server-side: verbatim) list reconciles. On error TanStack rolls
 * the cache back to the snapshot and the global `MutationCache` auto-toasts.
 */
export function useUpdateColumnPreferenceMutation(tableKey: ColumnTableKey) {
  const qc = useQueryClient();
  const key = columnPreferenceKeys.columns(tableKey);
  return useMutation<
    ColumnPreference,
    Error,
    string[],
    { previous?: ColumnPreference }
  >({
    mutationFn: (columnIds) => updateColumnPreference(tableKey, columnIds),
    onMutate: async (columnIds) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ColumnPreference>(key);
      qc.setQueryData<ColumnPreference>(key, { tableKey, columnIds });
      return { previous };
    },
    onError: (_err, _columnIds, ctx) => {
      if (ctx?.previous !== undefined) qc.setQueryData(key, ctx.previous);
    },
    onSuccess: (data) => {
      qc.setQueryData<ColumnPreference>(key, data);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}

/**
 * The listing page's "Export report" button — validates + resolves the
 * account scope synchronously (checked rows, or everything matching the
 * current filters) and enqueues the backend's async export job (resolve
 * selection → ClickHouse → CSV → email); the ClickHouse read, CSV build, and
 * email send all happen off this request now, unlike the single-account
 * sibling `useEspExportReportMutation` (`features/warmup-analytics/queries.ts`),
 * which stays fully synchronous. Read-only side effect on the server (no
 * account/table state changes), so no cache invalidation here. Success only
 * confirms the job was queued, not that the email has been sent — the
 * static toast copy below must not imply otherwise.
 */
export function useAllAccountsWarmupExportMutation() {
  return useMutation<
    { message: string },
    Error,
    {
      selection: BulkSelection;
      days?: WarmupExportDays;
      from?: string;
      to?: string;
      esps?: readonly EspValue[];
    }
  >({
    meta: { successMessage: "We're preparing your report. It will arrive in your inbox shortly." },
    mutationFn: (body) => postAllAccountsWarmupExport(body),
  });
}
